import { safeSetDoc, safeUpdateDoc, safeAddDoc, safeDeleteDoc } from '../utils';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Youtube,
  Plus,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Trash2,
  RefreshCw,
  Search,
  Play,
  X,
  Clock,
} from 'lucide-react';
import {
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  doc,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';

const WatchListenView = ({ user }) => {
  const [view, setView] = useState('feed'); // 'feed', 'sources', 'history'
  const [channels, setChannels] = useState([]);
  const [newChannelUrl, setNewChannelUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedChannelIds, setExpandedChannelIds] = useState({});
  const [activeVideo, setActiveVideo] = useState(null); // { videoId, title, channelId, channelName }
  const [playbackStates, setPlaybackStates] = useState({});
  const [watchHistory, setWatchHistory] = useState([]);
  const [localProgress, setLocalProgress] = useState(null); // { videoId, position, duration }
  const [expandedHistoryIds, setExpandedHistoryIds] = useState({});

  const playerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const sessionStartPosRef = useRef(null);
  const playbackStatesRef = useRef({});
  const currentSessionIdRef = useRef(null);

  useEffect(() => {
    playbackStatesRef.current = playbackStates;
    window.testPlaybackStates = playbackStates;
  }, [playbackStates]);

  useEffect(() => {
    const uid = user?.uid || 'test-uid-123';

    // Listen for channels
    const qChannels = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'youtube_channels'),
      where('addedBy', '==', uid)
    );
    const unsubChannels = onSnapshot(qChannels, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      setChannels(docs);
    });

    // Listen for playback states
    const qPlayback = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'playback_states'),
      where('userId', '==', uid)
    );
    const unsubPlayback = onSnapshot(qPlayback, (snap) => {
      const states = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        states[data.videoId] = data;
      });
      setPlaybackStates(states);
    });

    // Listen for watch history
    const qHistory = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'watch_history'),
      where('userId', '==', uid),
      orderBy('watchedAt', 'desc'),
      limit(50)
    );
    const unsubHistory = onSnapshot(qHistory, (snap) => {
      setWatchHistory(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    });

    return () => {
      unsubChannels();
      unsubPlayback();
      unsubHistory();
    };
  }, [user]);

  // Aggregate all videos for the Feed
  const feedVideos = useMemo(() => {
    const all = [];
    channels.forEach((ch) => {
      if (ch.videos) {
        ch.videos.forEach((v) => {
          all.push({
            ...v,
            channelId: ch.channelId,
            channelName: ch.name,
            sourceId: ch.id,
          });
        });
      }
    });
    return all.sort((a, b) => new Date(b.published) - new Date(a.published));
  }, [channels]);

  const savePlaybackPosition = async (videoId, position, duration, completed = false) => {
    const uid = user?.uid || 'test-uid-123';
    const docId = `${uid}_${videoId}`;

    // Use playbackStatesRef to avoid stale closures in the interval
    const existing = (playbackStatesRef?.current || {})[videoId] || {};
    const currentPos = Math.floor(position);

    // 1. Raw Session Logic (Specific history with overlaps)
    let rawSessions = existing.rawSessions ? [...existing.rawSessions.map((s) => ({ ...s }))] : [];
    const sessionId = currentSessionIdRef.current;
    const startPos = Math.floor(
      sessionStartPosRef.current !== null ? sessionStartPosRef.current : currentPos
    );

    if (sessionId) {
      const sIdx = rawSessions.findIndex((s) => s.id === sessionId);
      if (sIdx !== -1) {
        // Update existing session
        rawSessions[sIdx] = { ...rawSessions[sIdx], end: currentPos, timestamp: Date.now() };
      } else {
        rawSessions.push({
          id: sessionId,
          start: startPos,
          end: currentPos,
          timestamp: Date.now(),
        });
      }
    } else if (!completed && currentPos > startPos) {
      // New session
      const newId = `s_${Date.now()}`;
      currentSessionIdRef.current = newId;
      rawSessions.push({ id: newId, start: startPos, end: currentPos, timestamp: Date.now() });
    }

    // 2. Heatmap Segment Logic (Merged from ALL raw sessions)
    let newHeatmap = [];
    const buffer = 10;
    const sortedRaw = [...rawSessions]
      .filter((s) => s.end > s.start)
      .sort((a, b) => a.start - b.start);
    if (sortedRaw.length > 0) {
      let current = [sortedRaw[0].start, sortedRaw[0].end];
      for (let i = 1; i < sortedRaw.length; i++) {
        const s = sortedRaw[i];
        if (s.start <= current[1] + buffer) {
          current[1] = Math.max(current[1], s.end);
        } else {
          newHeatmap.push(current);
          current = [s.start, s.end];
        }
      }
      newHeatmap.push(current);
    } else if (currentPos > 0 && !completed) {
      newHeatmap = [[currentPos, currentPos]];
    }

    await safeSetDoc(
      doc(db, 'artifacts', appId, 'public', 'data', 'playback_states', docId),
      {
        userId: uid,
        videoId,
        position: currentPos,
        duration,
        segments: newHeatmap,
        rawSessions: rawSessions,
        completed,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const addToHistory = async (video) => {
    const uid = user?.uid || 'test-uid-123';
    const historyId = `${uid}_${video.videoId}`;
    await safeSetDoc(
      doc(db, 'artifacts', appId, 'public', 'data', 'watch_history', historyId),
      {
        userId: uid,
        videoId: video.videoId,
        title: video.title,
        channelId: video.channelId,
        channelName: video.channelName,
        watchedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  useEffect(() => {
    if (!activeVideo) {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setLocalProgress(null);
      sessionStartPosRef.current = null;
      currentSessionIdRef.current = null;
      return;
    }

    addToHistory(activeVideo);

    const videoId = activeVideo.videoId;
    const uid = user?.uid || 'test-uid-123';

    const onPlayerReady = (event) => {
      window.testPlayer = event.target;
      const state = (playbackStatesRef?.current || {})[videoId];
      if (state && state.position > 0 && !state.completed) {
        event.target.seekTo(state.position);
      }
      sessionStartPosRef.current = event.target.getCurrentTime();
      currentSessionIdRef.current = null;
    };

    const onPlayerStateChange = (event) => {
      if (event.data === 1) {
        // Playing
        // Small delay to ensure player clock is synced after seek/start
        setTimeout(() => {
          const ct = event.target.getCurrentTime();
          sessionStartPosRef.current = ct;
          // Force a fresh sessionId when we start playing
          currentSessionIdRef.current = `s_${Date.now()}`;

          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = setInterval(() => {
            const currentTime = event.target.getCurrentTime();
            const duration = event.target.getDuration();
            // Only track if we have valid non-zero time
            if (Math.floor(currentTime) > 0) {
              setLocalProgress({ videoId, position: currentTime, duration });
              if (Math.floor(currentTime) % 15 === 0) {
                savePlaybackPosition(videoId, currentTime, duration);
              }
            }
          }, 1000);
        }, 500);
      } else {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (event.data === 2) {
          const currentTime = event.target.getCurrentTime();
          const duration = event.target.getDuration();
          if (Math.floor(currentTime) > 0) {
            savePlaybackPosition(videoId, currentTime, duration);
          }
        }
        // Clear sessionId on pause/seek to force a new interval on next play
        currentSessionIdRef.current = null;
        sessionStartPosRef.current = null;
      }

      if (event.data === 0) {
        // Ended
        // Mark completed but don't add a 0-pos session
        const docId = `${uid}_${videoId}`;
        safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'playback_states', docId), {
          completed: true,
          position: 0,
          updatedAt: serverTimestamp(),
        });
        setLocalProgress(null);
        currentSessionIdRef.current = null;
        sessionStartPosRef.current = null;
      }
    };

    const initPlayer = () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          /* ignore */
        }
      }
      playerRef.current = new window.YT.Player('youtube-player', {
        height: '100%',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
        },
        events: {
          onReady: (event) => {
            window.testPlayer = event.target;
            onPlayerReady(event);
          },
          onStateChange: onPlayerStateChange,
        },
      });
    };

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      window.onYouTubeIframeAPIReady = initPlayer;
    } else {
      initPlayer();
    }

    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (e) {
          /* ignore */
        }
      }
    };
  }, [activeVideo, user]);

  const resolveChannelId = async (inputUrl) => {
    let url = inputUrl.trim();
    if (!url.startsWith('http')) {
      if (url.startsWith('@')) url = `https://www.youtube.com/${url}`;
      else url = `https://www.youtube.com/channel/${url}`;
    }
    try {
      const proxyUrl = `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      const html = await response.text();
      const channelIdMatch =
        html.match(/"channelId":"(UC[a-zA-Z0-9_-]+)"/) ||
        html.match(/itemprop="channelId" content="(UC[a-zA-Z0-9_-]+)"/) ||
        html.match(/"externalId":"(UC[a-zA-Z0-9_-]+)"/);
      const channelNameMatch =
        html.match(/"name":"([^"]+)"/) || html.match(/<title>([^<]+) - YouTube<\/title>/);
      if (channelIdMatch)
        return { id: channelIdMatch[1], name: channelNameMatch ? channelNameMatch[1] : null };
      return null;
    } catch (e) {
      return null;
    }
  };

  const fetchVideos = async (channelId) => {
    const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
    try {
      const proxyUrl = `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(rssUrl)}`;
      const response = await fetch(proxyUrl);
      const text = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');
      const entries = xmlDoc.getElementsByTagName('entry');
      const videos = [];
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const videoId = entry.getElementsByTagName('yt:videoId')[0]?.textContent;
        const title = entry.getElementsByTagName('title')[0]?.textContent;
        const published = entry.getElementsByTagName('published')[0]?.textContent;
        if (videoId)
          videos.push({
            videoId,
            title,
            published,
            url: `https://www.youtube.com/watch?v=${videoId}`,
          });
      }
      return videos;
    } catch (e) {
      return [];
    }
  };

  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!newChannelUrl.trim()) return;
    setLoading(true);
    let resolved = await resolveChannelId(newChannelUrl);
    if (!resolved) {
      alert('Could find channel.');
      setLoading(false);
      return;
    }
    const videos = await fetchVideos(resolved.id);
    await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'youtube_channels'), {
      channelId: resolved.id,
      url: newChannelUrl,
      name: resolved.name || resolved.id,
      videos: videos,
      lastFetched: serverTimestamp(),
      addedBy: user?.uid || 'test-uid-123',
    });
    setNewChannelUrl('');
    setLoading(false);
  };

  const toggleExpand = async (id, channelId, existingVideos) => {
    setExpandedChannelIds((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!expandedChannelIds[id]) {
      const newVideos = await fetchVideos(channelId);
      const merged = [...(existingVideos || [])];
      newVideos.forEach((nv) => {
        if (!merged.find((mv) => mv.videoId === nv.videoId)) merged.unshift(nv);
      });
      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'youtube_channels', id), {
        videos: merged,
        lastFetched: serverTimestamp(),
      });
    }
  };

  const handleDeleteChannel = async (id) => {
    if (window.confirm('Remove?'))
      await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'youtube_channels', id));
  };

  const deleteRawSession = async (videoId, sessionId) => {
    const uid = user?.uid || 'test-uid-123';
    const docId = `${uid}_${videoId}`;
    const existing = playbackStates[videoId];
    if (!existing || !existing.rawSessions) return;

    const nextRaw = existing.rawSessions.filter((s) => s.id !== sessionId);

    let newHeatmap = [];
    const buffer = 10;
    const sortedRaw = [...nextRaw].filter((s) => s.end > s.start).sort((a, b) => a.start - b.start);
    if (sortedRaw.length > 0) {
      let current = [sortedRaw[0].start, sortedRaw[0].end];
      for (let i = 1; i < sortedRaw.length; i++) {
        const s = sortedRaw[i];
        if (s.start <= current[1] + buffer) {
          current[1] = Math.max(current[1], s.end);
        } else {
          newHeatmap.push(current);
          current = [s.start, s.end];
        }
      }
      newHeatmap.push(current);
    }

    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'playback_states', docId), {
      rawSessions: nextRaw,
      segments: newHeatmap,
    });
  };

  const toggleHistory = (videoId) => {
    setExpandedHistoryIds((prev) => ({ ...prev, [videoId]: !prev[videoId] }));
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleProgressClick = (e, videoId, duration) => {
    if (!duration || !playerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    playerRef.current.seekTo(duration * percent);
  };

  const renderPlaybackProgress = (videoId, duration) => {
    const state = playbackStates[videoId];
    const isActive = activeVideo?.videoId === videoId;
    const currentPos =
      isActive && localProgress?.videoId === videoId
        ? localProgress.position
        : state?.position || 0;

    if (!state && !isActive) return <div className="h-1.5 mt-2 bg-zinc-50 rounded-full" />;

    let segments = state?.segments ? [...state.segments.map((s) => [...s])] : [];

    // Visual merging for active video only
    if (isActive && currentPos > 0 && sessionStartPosRef.current !== null) {
      const start = Math.floor(sessionStartPosRef.current);
      const end = Math.floor(currentPos);

      let merged = false;
      segments = segments.map((seg) => {
        if (end >= seg[0] - 10 && start <= seg[1] + 10) {
          merged = true;
          return [Math.min(seg[0], start), Math.max(seg[1], end)];
        }
        return seg;
      });
      if (!merged && end > start) segments.push([start, end]);
      segments.sort((a, b) => a[0] - b[0]);
    } else if (segments.length === 0 && currentPos > 0) {
      segments = [[currentPos, currentPos]];
    }

    const totalDuration = duration || state?.duration || (isActive ? localProgress?.duration : 0);
    if (!totalDuration || segments.length === 0)
      return <div className="h-1.5 mt-2 bg-zinc-50 rounded-full" />;

    return (
      <div
        data-testid={`progress-bar-${videoId}`}
        data-segments={segments.length}
        className="w-full h-1.5 bg-zinc-100 rounded-full mt-2 overflow-hidden relative group/progress cursor-crosshair"
        onClick={(e) => handleProgressClick(e, videoId, totalDuration)}
      >
        {segments.map((seg, idx) => {
          const startP = (seg[0] / totalDuration) * 100;
          const endP = (seg[1] / totalDuration) * 100;
          return (
            <div
              key={idx}
              className={`absolute h-full bg-red-500 rounded-full transition-all ${isActive ? 'duration-1000 linear' : 'duration-300'} segment-indicator`}
              style={{ left: `${startP}%`, width: `${Math.max(0.5, endP - startP)}%` }}
            />
          );
        })}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-zinc-900 opacity-0 group-hover/progress:opacity-50 transition-opacity"
          style={{ left: 'var(--mouse-x, 0%)' }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.parentElement.getBoundingClientRect();
            e.currentTarget.style.setProperty(
              '--mouse-x',
              `${((e.clientX - rect.left) / rect.width) * 100}%`
            );
          }}
        />
      </div>
    );
  };

  const renderHistoryList = (videoId) => {
    const state = playbackStates[videoId];
    if (!state || !state.rawSessions) return null;

    const validSessions = state.rawSessions.filter((s) => s.end > s.start);
    if (validSessions.length === 0) return null;

    const sessions = [...validSessions].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return (
      <div className="mt-4 border-t border-zinc-50 pt-4 space-y-2 animate-in slide-in-from-top-2 duration-300">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Clock size={10} className="text-zinc-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              Watch History Segments
            </span>
          </div>
          <button
            onClick={() => toggleHistory(videoId)}
            className="text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Hide
          </button>
        </div>
        <div className="grid grid-cols-1 gap-1">
          {sessions.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between p-2 bg-zinc-50/50 rounded-xl hover:bg-zinc-50 transition-all group/seg"
            >
              <button
                onClick={() => playerRef.current?.seekTo(s.start)}
                className="flex items-center gap-3 text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-white flex items-center justify-center text-zinc-400 group-hover/seg:text-red-600 transition-colors shadow-sm">
                  <Play size={10} fill="currentColor" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-black text-zinc-800">
                    {formatTime(s.start)} - {formatTime(s.end)}
                  </span>
                  <span className="text-[9px] font-bold text-zinc-400">
                    {new Date(s.timestamp).toLocaleString()}
                  </span>
                </div>
              </button>
              <button
                onClick={() => deleteRawSession(videoId, s.id)}
                className="p-2 text-zinc-300 hover:text-red-600 opacity-0 group-hover/seg:opacity-100 transition-all"
              >
                <X size={14} strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 h-full bg-white overflow-y-auto p-10 custom-scrollbar">
      <div className="max-w-4xl mx-auto space-y-10">
        <header className="flex items-center justify-between border-b border-zinc-100 pb-6">
          <div className="flex flex-col">
            <h1
              data-testid="watch-heading"
              className="text-3xl font-black text-zinc-900 tracking-tighter uppercase"
            >
              Watch / Listen
            </h1>
            <p className="text-sm text-zinc-400 font-medium">Archived channel video feeds</p>
          </div>{' '}
          <div className="p-3 bg-red-50 text-red-600 rounded-2xl">
            <Youtube size={32} strokeWidth={2.5} />
          </div>
        </header>
        <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-2xl w-fit">
          {['feed', 'sources', 'history'].map((id) => (
            <button
              key={id}
              onClick={() => setView(id)}
              className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${view === id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              {id}
            </button>
          ))}
        </div>

        {activeVideo ? (
          <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-black aspect-video border border-zinc-100 group">
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute top-4 right-4 p-2 bg-black/60 backdrop-blur hover:bg-red-600 text-white rounded-full transition-all z-10 opacity-0 group-hover:opacity-100"
              >
                <X size={20} strokeWidth={2.5} />
              </button>
              <div id="youtube-player" className="w-full h-full" />
            </div>
            <div className="px-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest truncate max-w-[60%]">
                  {activeVideo.title} • {activeVideo.channelName}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleHistory(activeVideo.videoId)}
                    className={`text-[10px] font-black uppercase tracking-widest transition-colors ${expandedHistoryIds[activeVideo.videoId] ? 'text-red-600' : 'text-zinc-400 hover:text-zinc-600'}`}
                  >
                    {expandedHistoryIds[activeVideo.videoId] ? 'Hide History' : 'Show History'}
                  </button>
                  <span className="text-[10px] font-black text-red-600 uppercase tracking-widest whitespace-nowrap">
                    Active Playing
                  </span>
                </div>
              </div>
              {renderPlaybackProgress(activeVideo.videoId)}
              {expandedHistoryIds[activeVideo.videoId] && renderHistoryList(activeVideo.videoId)}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="relative rounded-3xl overflow-hidden bg-zinc-50 aspect-video border-2 border-dashed border-zinc-100 flex flex-col items-center justify-center gap-4 text-zinc-300">
              <div className="p-8 bg-white rounded-full shadow-sm">
                <Youtube size={64} strokeWidth={1.5} className="opacity-20" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                Select a video to start watching
              </span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {view === 'sources' && (
            <form onSubmit={handleAddChannel} className="flex gap-3">
              <input
                type="text"
                placeholder="Paste Channel URL"
                className="flex-1 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:border-red-500 transition-all"
                value={newChannelUrl}
                onChange={(e) => setNewChannelUrl(e.target.value)}
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-red-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-red-700 transition-all flex items-center gap-2 shadow-lg shadow-red-100"
              >
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={16} />}Add
                Source
              </button>
            </form>
          )}
          {view === 'feed' && (
            <div className="grid grid-cols-1 gap-3">
              {feedVideos.map((video, idx) => (
                <div
                  key={`${video.videoId}_${idx}`}
                  className="w-full flex flex-col p-4 bg-white border border-zinc-100 rounded-2xl hover:border-red-200 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setActiveVideo(video)}
                      className="flex items-center gap-4 overflow-hidden flex-1 text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-400 group-hover:bg-red-50 group-hover:text-red-600 transition-colors shrink-0">
                        <Play size={20} fill="currentColor" />
                      </div>
                      <div className="flex flex-col overflow-hidden flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">
                            {video.channelName}
                          </span>
                          <span className="text-[10px] font-bold text-zinc-300">•</span>
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                            {new Date(video.published).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="text-sm font-black text-zinc-800 truncate group-hover:text-zinc-900">
                          {video.title}
                        </span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHistory(video.videoId);
                        }}
                        className={`p-2 transition-colors ${expandedHistoryIds[video.videoId] ? 'text-red-500' : 'text-zinc-300 hover:text-zinc-600'}`}
                        title="Watch Session History"
                      >
                        <Search size={18} />
                      </button>
                      <div
                        className="p-2 text-zinc-200 group-hover:text-red-500 transition-colors cursor-pointer"
                        onClick={() => setActiveVideo(video)}
                      >
                        <ExternalLink size={18} />
                      </div>
                    </div>
                  </div>
                  {renderPlaybackProgress(video.videoId)}
                  {expandedHistoryIds[video.videoId] && renderHistoryList(video.videoId)}
                </div>
              ))}
            </div>
          )}
          {view === 'sources' && (
            <div className="space-y-4">
              {channels.map((channel) => (
                <div
                  key={channel.id}
                  className="border border-zinc-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                  <div
                    className="flex items-center justify-between p-4 bg-white cursor-pointer hover:bg-zinc-50 transition-colors"
                    onClick={() => toggleExpand(channel.id, channel.channelId, channel.videos)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-red-50 text-red-600 rounded-xl">
                        <Play size={20} fill="currentColor" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-zinc-900 tracking-tight">
                          {channel.name}
                        </span>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                          ID: {channel.channelId}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-zinc-400">
                      <span className="text-[10px] font-black">
                        {channel.videos?.length || 0} Videos
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteChannel(channel.id);
                        }}
                        className="hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                      {expandedChannelIds[channel.id] ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                    </div>
                  </div>
                  {expandedChannelIds[channel.id] && (
                    <div className="border-t border-zinc-100 bg-zinc-50/30 p-4 space-y-2 animate-in slide-in-from-top-2 duration-300">
                      {channel.videos?.map((video, idx) => (
                        <div
                          key={idx}
                          className="w-full flex flex-col p-3 bg-white border border-zinc-100 rounded-xl hover:border-red-200 hover:shadow-sm transition-all group"
                        >
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() =>
                                setActiveVideo({
                                  ...video,
                                  channelId: channel.channelId,
                                  channelName: channel.name,
                                })
                              }
                              className="flex items-center gap-3 overflow-hidden flex-1 text-left"
                            >
                              <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 group-hover:bg-red-50 group-hover:text-red-600 transition-colors shrink-0">
                                <Play size={14} fill="currentColor" />
                              </div>
                              <div className="flex flex-col overflow-hidden flex-1">
                                <span className="text-xs font-bold text-zinc-700 truncate group-hover:text-zinc-900">
                                  {video.title}
                                </span>
                                <span className="text-[9px] font-medium text-zinc-400">
                                  {new Date(video.published).toLocaleDateString()}
                                </span>
                              </div>
                            </button>
                            <Play
                              size={14}
                              className="text-zinc-300 group-hover:text-red-600 shrink-0 cursor-pointer"
                              onClick={() =>
                                setActiveVideo({
                                  ...video,
                                  channelId: channel.channelId,
                                  channelName: channel.name,
                                })
                              }
                            />
                          </div>
                          {renderPlaybackProgress(video.videoId)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {view === 'history' && (
            <div className="grid grid-cols-1 gap-3">
              {watchHistory.map((item, idx) => (
                <div
                  key={`${item.id}_${idx}`}
                  className="w-full flex flex-col p-4 bg-white border border-zinc-100 rounded-2xl hover:border-red-200 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() =>
                        setActiveVideo({
                          videoId: item.videoId,
                          title: item.title,
                          channelId: item.channelId,
                          channelName: item.channelName,
                        })
                      }
                      className="flex items-center gap-4 overflow-hidden flex-1 text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-400 group-hover:bg-red-50 group-hover:text-red-600 transition-colors shrink-0">
                        <RefreshCw size={20} />
                      </div>
                      <div className="flex flex-col overflow-hidden flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">
                            {item.channelName}
                          </span>
                          <span className="text-[10px] font-bold text-zinc-300">•</span>
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                            {item.watchedAt?.seconds
                              ? new Date(item.watchedAt.seconds * 1000).toLocaleString()
                              : 'Just now'}
                          </span>
                        </div>
                        <span className="text-sm font-black text-zinc-800 truncate group-hover:text-zinc-900">
                          {item.title}
                        </span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHistory(item.videoId);
                        }}
                        className={`p-2 transition-colors ${expandedHistoryIds[item.videoId] ? 'text-red-500' : 'text-zinc-300 hover:text-zinc-600'}`}
                        title="Watch Session History"
                      >
                        <Search size={18} />
                      </button>
                      <div
                        className="p-2 text-zinc-200 group-hover:text-red-500 transition-colors cursor-pointer"
                        onClick={() =>
                          setActiveVideo({
                            videoId: item.videoId,
                            title: item.title,
                            channelId: item.channelId,
                            channelName: item.channelName,
                          })
                        }
                      >
                        <Play size={18} fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  {renderPlaybackProgress(item.videoId)}
                  {expandedHistoryIds[item.videoId] && renderHistoryList(item.videoId)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchListenView;
